import { createFileRoute } from "@tanstack/react-router";
import { type ColumnDef, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import ChevronDownIcon from "lucide-react/dist/esm/icons/chevron-down";
import FilePlus2Icon from "lucide-react/dist/esm/icons/file-plus-2";
import { useCallback, useMemo } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { DataTable } from "@/components/data-table/data-table";
import { DataTablePagination } from "@/components/data-table/data-table-pagination";
import { DataTableToolbar } from "@/components/data-table/data-table-toolbar";
import { FilterSelect } from "@/components/data-table/filter-controls";
import { useDensity } from "@/components/data-table/use-density";
import { PageBody, PageHeader, Panel } from "@/components/shell/page-frame";
import { EmptyState } from "@/components/states/state-views";
import { StatusBadge } from "@/components/status/status-badge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
  createReport,
  REPORT_TYPE_LABEL,
  type Report,
  type ReportType,
  useReports
} from "@/features/analytics/reports";
import {
  formatCurrency,
  formatDate,
  formatDelta,
  formatInteger,
  formatPercent
} from "@/lib/format";
import { cn } from "@/lib/utils";

const reportsSearchSchema = z.object({
  type: z.enum(["all", "weekly", "monthly"]).catch("all").default("all"),
  /** 対象期間（開始日）の年・月。未指定（すべて）は URL に載せない */
  year: z.coerce.number().int().optional().catch(undefined),
  month: z.coerce.number().int().min(1).max(12).optional().catch(undefined),
  page: z.coerce.number().int().min(1).catch(1).default(1),
  pageSize: z.coerce.number().int().min(25).max(100).catch(25).default(25)
});

type ReportsSearch = z.infer<typeof reportsSearchSchema>;

/** グループ見出しに使う年月。UTC 変換で月がずれないようローカル時刻で読む。 */
function yearMonthLabel(iso: string): string {
  const date = new Date(iso);
  return `${date.getFullYear()}年${date.getMonth() + 1}月`;
}

function yearMonthIndex(iso: string): number {
  const date = new Date(iso);
  return date.getFullYear() * 12 + date.getMonth();
}

export const Route = createFileRoute("/analytics/")({
  validateSearch: reportsSearchSchema,
  component: ReportListPage
});

/**
 * 週次・月次レポートの一覧。個別レポートの中身は詳細（$reportId）で読む。
 * 定時生成が基本で、抜けた期間だけ「レポートを作成」で手動生成する。
 */
function ReportListPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const reports = useReports();
  const { density, setDensity } = useDensity();

  const openReport = useCallback(
    (id: string) => {
      void navigate({ to: "/analytics/$reportId", params: { reportId: id } });
    },
    [navigate]
  );

  const update = (patch: Partial<ReportsSearch>) => {
    void navigate({ search: (previous) => ({ ...previous, ...patch }) });
  };

  const handleCreate = (type: ReportType) => {
    const { report, created } = createReport(type, (ready) => {
      toast.success(`${ready.title}を作成しました`, {
        action: { label: "開く", onClick: () => openReport(ready.id) }
      });
    });
    if (!created) {
      toast.info(`${report.title}は作成済みです`, {
        action: { label: "開く", onClick: () => openReport(report.id) }
      });
    }
  };

  // 年月グループが連続するよう固定順に並べる（新しい月 → 月のまとめ（月次）→ 週の新しい順）
  const data = useMemo(() => {
    const filtered = reports.filter((report) => {
      if (search.type !== "all" && report.type !== search.type) return false;
      const start = new Date(report.periodStart);
      if (search.year !== undefined && start.getFullYear() !== search.year) return false;
      if (search.month !== undefined && start.getMonth() + 1 !== search.month) return false;
      return true;
    });
    return filtered.sort((a, b) => {
      const monthDiff = yearMonthIndex(b.periodStart) - yearMonthIndex(a.periodStart);
      if (monthDiff !== 0) return monthDiff;
      if (a.type !== b.type) return a.type === "monthly" ? -1 : 1;
      return new Date(b.periodStart).getTime() - new Date(a.periodStart).getTime();
    });
  }, [reports, search.type, search.year, search.month]);

  const pageRows = useMemo(() => {
    const start = (search.page - 1) * search.pageSize;
    return data.slice(start, start + search.pageSize);
  }, [data, search.page, search.pageSize]);

  // 年の選択肢はデータから作る。存在しない年を選ばせても空になるだけなので出さない。
  const yearOptions = useMemo(
    () =>
      [...new Set(reports.map((report) => new Date(report.periodStart).getFullYear()))].sort(
        (a, b) => b - a
      ),
    [reports]
  );

  const columns = useMemo<ColumnDef<Report>[]>(
    () => [
      {
        id: "type",
        accessorKey: "type",
        header: "種別",
        meta: { label: "種別", width: "72px" },
        cell: ({ row }) => (
          <Badge variant={row.original.type === "weekly" ? "secondary" : "outline"}>
            {REPORT_TYPE_LABEL[row.original.type]}
          </Badge>
        )
      },
      {
        id: "title",
        accessorKey: "title",
        header: "レポート",
        meta: { label: "レポート名" },
        cell: ({ row }) => (
          <span className="truncate font-medium text-fg">{row.original.title}</span>
        )
      },
      {
        id: "status",
        accessorKey: "status",
        header: "状態",
        meta: { label: "状態", width: "88px" },
        cell: ({ row }) =>
          row.original.status === "generating" ? (
            <StatusBadge tone="info">生成中</StatusBadge>
          ) : (
            <StatusBadge tone="success">作成済</StatusBadge>
          )
      },
      {
        id: "revenue",
        accessorKey: "revenue",
        header: "帰属売上",
        meta: { label: "帰属売上", align: "end", width: "120px" },
        cell: ({ row }) => (
          <span className="num font-semibold text-fg">{formatCurrency(row.original.revenue)}</span>
        )
      },
      {
        id: "revenueDelta",
        accessorKey: "revenueDeltaPercent",
        header: "前期比",
        meta: { label: "帰属売上の前期比", align: "end", width: "88px", priority: "medium" },
        cell: ({ row }) => {
          const delta = row.original.revenueDeltaPercent;
          return (
            <span
              className={cn(
                "num",
                delta > 0 ? "text-success-fg" : delta < 0 ? "text-critical-fg" : "text-fg-muted"
              )}
            >
              {formatDelta(delta)}
            </span>
          );
        }
      },
      {
        id: "openRate",
        accessorKey: "openRate",
        header: "開封率",
        meta: { label: "開封率", align: "end", width: "80px", priority: "medium" },
        cell: ({ row }) => <span className="num">{formatPercent(row.original.openRate)}</span>
      },
      {
        id: "createdAt",
        accessorFn: (report) => new Date(report.createdAt).getTime(),
        header: "作成日",
        meta: { label: "作成日", align: "end", width: "104px", priority: "low" },
        cell: ({ row }) => (
          <time dateTime={row.original.createdAt} className="num text-fg-muted">
            {formatDate(row.original.createdAt)}
          </time>
        )
      },
      {
        id: "open",
        enableSorting: false,
        enableHiding: false,
        header: "",
        meta: { label: "操作", width: "72px" },
        cell: ({ row }) => (
          <div className="flex justify-end">
            <Button
              type="button"
              size="xs"
              variant="ghost"
              onClick={() => openReport(row.original.id)}
            >
              開く
            </Button>
          </div>
        )
      }
    ],
    [openReport]
  );

  const table = useReactTable({
    data: pageRows,
    columns,
    getRowId: (report) => report.id,
    // 年月グループの連続性を保つため、列ソートは持たせない
    enableSorting: false,
    getCoreRowModel: getCoreRowModel()
  });

  return (
    <>
      <PageHeader
        kind="analysis"
        title="レポート"
        description="週次・月次レポートの一覧。開くと期間中のKPIとAI要約を確認できる"
        action={
          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <Button size="sm">
                  <FilePlus2Icon aria-hidden="true" />
                  レポートを作成
                  <ChevronDownIcon aria-hidden="true" />
                </Button>
              }
            />
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>作成するレポート</DropdownMenuLabel>
              {(Object.keys(REPORT_TYPE_LABEL) as ReportType[]).map((type) => (
                <DropdownMenuItem key={type} onClick={() => handleCreate(type)}>
                  <span className="flex flex-col">
                    <span>{REPORT_TYPE_LABEL[type]}レポートを作成</span>
                    <span className="text-2xs text-fg-subtle">
                      {type === "weekly" ? "先週（月〜日）を対象に生成" : "先月を対象に生成"}
                    </span>
                  </span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        }
      />

      <PageBody density="dense">
        <Panel title="レポート一覧" count={formatInteger(data.length)} flush>
          <DataTableToolbar table={table} density={density} onDensityChange={setDensity}>
            <FilterSelect
              label="種別"
              value={search.type}
              onChange={(value) => update({ type: value as ReportsSearch["type"], page: 1 })}
              width="w-32"
              options={[
                { value: "all", label: "すべて" },
                { value: "weekly", label: "週次" },
                { value: "monthly", label: "月次" }
              ]}
            />
            <FilterSelect
              label="年"
              value={search.year === undefined ? "all" : String(search.year)}
              onChange={(value) =>
                update({ year: value === "all" ? undefined : Number(value), page: 1 })
              }
              width="w-28"
              options={[
                { value: "all", label: "すべて" },
                ...yearOptions.map((year) => ({ value: String(year), label: `${year}年` }))
              ]}
            />
            <FilterSelect
              label="月"
              value={search.month === undefined ? "all" : String(search.month)}
              onChange={(value) =>
                update({ month: value === "all" ? undefined : Number(value), page: 1 })
              }
              width="w-24"
              options={[
                { value: "all", label: "すべて" },
                ...Array.from({ length: 12 }, (_, index) => ({
                  value: String(index + 1),
                  label: `${index + 1}月`
                }))
              ]}
            />
          </DataTableToolbar>

          <DataTable
            table={table}
            density={density}
            maxHeight="calc(100vh - 300px)"
            rowGroup={(row) => yearMonthLabel(row.original.periodStart)}
            onRowActivate={(row) => openReport(row.original.id)}
            activateOn="click"
            emptyState={
              <EmptyState
                title="条件に一致するレポートがありません"
                description="絞り込みを解除するか、右上の「レポートを作成」から週次・月次レポートを生成してください。"
                action={
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      update({ type: "all", year: undefined, month: undefined, page: 1 })
                    }
                  >
                    絞り込みを解除
                  </Button>
                }
              />
            }
          />

          <DataTablePagination
            page={search.page}
            pageSize={search.pageSize}
            totalCount={data.length}
            onPageChange={(page) => update({ page })}
            onPageSizeChange={(pageSize) => update({ pageSize, page: 1 })}
          />
        </Panel>
      </PageBody>
    </>
  );
}
